/**
 * Hook for appointment scheduling functionality
 * Handles resource/facility selection, date availability, and time slot management
 */

import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { 
  appointmentSchedulingService,
  Resource,
  Facility,
  ResourceTimeSlot,
  BookedAppointment
} from '@/services/api/appointments/appointmentSchedulingService';

export type AppointmentType = 'virtual' | 'in-person' | null;

export interface SelectedTimeRange {
  startTime: string;
  endTime: string;
  availabilityDayTimeId: number;
}

// Extended time slot with converted times for display
export interface DisplayTimeSlot {
  availabilityDayTimeId: number;
  startTime: string;
  endTime: string;
  displayTime: string;
  bookedCount: number;
  userId?: number;
  resourceTimezone?: string;
  convertedDisplayTime?: string;
}

export interface AppointmentSchedulingState {
  appointmentType: AppointmentType;
  selectedResource: Resource | null;
  selectedFacility: Facility | null;
  selectedDate: Date | undefined;
  selectedTimeRange: SelectedTimeRange | null;
  resources: Resource[];
  facilities: Facility[];
  availableDates: string[];
  timeSlots: DisplayTimeSlot[];
  bookedAppointments: BookedAppointment[];
  patientTimezone: string;
  loadingResources: boolean;
  loadingFacilities: boolean;
  loadingDates: boolean;
  loadingTimeSlots: boolean;
  error: string | null;
  conflictWarning: string | null;
}

export const useAppointmentScheduling = (clinicId?: number, patientTimezone?: string) => {
  const { hasuraToken, user, token } = useAuth();
  
  const [state, setState] = useState<AppointmentSchedulingState>({
    appointmentType: null,
    selectedResource: null,
    selectedFacility: null,
    selectedDate: undefined,
    selectedTimeRange: null,
    resources: [],
    facilities: [],
    availableDates: [],
    timeSlots: [],
    bookedAppointments: [],
    patientTimezone: patientTimezone || '',
    loadingResources: false,
    loadingFacilities: false,
    loadingDates: false,
    loadingTimeSlots: false,
    error: null,
    conflictWarning: null
  });

  // Update patientTimezone in state when prop changes
  React.useEffect(() => {
    setState(prev => ({
      ...prev,
      patientTimezone: patientTimezone || ''
    }));
  }, [patientTimezone]);

  // Current month state for calendar
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // Reset all state when appointment type changes
  const handleAppointmentTypeChange = useCallback((type: AppointmentType) => {
    setState(prev => ({
      ...prev,
      appointmentType: type,
      selectedResource: null,
      selectedFacility: null,
      selectedDate: undefined,
      selectedTimeRange: null,
      resources: [],
      facilities: [],
      availableDates: [],
      timeSlots: [],
      bookedAppointments: [],
      error: null,
      conflictWarning: null
    }));
    setCurrentMonth(new Date());
  }, []);

  // Fetch resources for virtual appointments
  const fetchResources = useCallback(async () => {
    if (!hasuraToken) return;
    
    setState(prev => ({ ...prev, loadingResources: true, error: null }));
    
    try {
      const resources = await appointmentSchedulingService.getResources(hasuraToken);
      setState(prev => ({ ...prev, resources, loadingResources: false }));
    } catch (error) {
      console.error('Error fetching resources:', error);
      setState(prev => ({ 
        ...prev, 
        loadingResources: false, 
        error: 'Failed to fetch resources' 
      }));
    }
  }, [hasuraToken]);

  // Fetch facilities for in-person appointments
  const fetchFacilities = useCallback(async () => {
    if (!hasuraToken || !clinicId) return;
    
    setState(prev => ({ ...prev, loadingFacilities: true, error: null }));
    
    try {
      const facilities = await appointmentSchedulingService.getClinicFacilities(hasuraToken, clinicId);
      setState(prev => ({ ...prev, facilities, loadingFacilities: false }));
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setState(prev => ({ 
        ...prev, 
        loadingFacilities: false, 
        error: 'Failed to fetch facilities' 
      }));
    }
  }, [hasuraToken, clinicId]);

  // Fetch available dates for the current month
  const fetchAvailableDates = useCallback(async (year: number, month: number) => {
    const userId = user?.id ? parseInt(user.id) : null;
    if (!userId || !token) return;

    setState(prev => ({ ...prev, loadingDates: true, error: null }));

    try {
      if (state.appointmentType === 'virtual' && state.selectedResource) {
        const response = await appointmentSchedulingService.getResourceAvailability(
          userId,
          year,
          month,
          token,
          [state.selectedResource.id], // userIds array
          state.patientTimezone || undefined // Only pass if timezone is available
        );
        setState(prev => ({ 
          ...prev, 
          availableDates: response.data.availableDates,
          loadingDates: false 
        }));
      } else if (state.appointmentType === 'in-person' && state.selectedFacility) {
        const response = await appointmentSchedulingService.getFacilityAvailability(
          userId,
          state.selectedFacility.clinic_facility_id,
          year,
          month,
          token
        );
        setState(prev => ({ 
          ...prev, 
          availableDates: response.data.availableDates,
          loadingDates: false 
        }));
      }
    } catch (error) {
      console.error('Error fetching available dates:', error);
      setState(prev => ({ 
        ...prev, 
        loadingDates: false, 
        availableDates: [],
        error: 'Failed to fetch available dates' 
      }));
    }
  }, [user?.id, token, state.appointmentType, state.selectedResource, state.selectedFacility, state.patientTimezone]);

  // Convert resource time slots to display time slots
  const convertToDisplayTimeSlots = (resourceTimeSlots: Array<{
    userId: number;
    resourceTimezone: string;
    timeSlots: ResourceTimeSlot[];
  }>): DisplayTimeSlot[] => {
    const displaySlots: DisplayTimeSlot[] = [];
    
    for (const resource of resourceTimeSlots) {
      for (const slot of resource.timeSlots) {
        displaySlots.push({
          availabilityDayTimeId: slot.availabilityDayTimeId,
          startTime: slot.convertedStartTime,
          endTime: slot.convertedEndTime,
          displayTime: slot.convertedDisplayTime,
          bookedCount: slot.bookedCount,
          userId: resource.userId,
          resourceTimezone: resource.resourceTimezone,
          convertedDisplayTime: slot.convertedDisplayTime
        });
      }
    }
    
    return displaySlots;
  };

  // Fetch time slots for selected date
  const fetchTimeSlots = useCallback(async (date: string) => {
    const userId = user?.id ? parseInt(user.id) : null;
    if (!userId || !token) return;

    setState(prev => ({ ...prev, loadingTimeSlots: true, error: null }));

    try {
      if (state.appointmentType === 'virtual' && state.selectedResource) {
        const response = await appointmentSchedulingService.getResourceTimeSlots(
          userId,
          date,
          token,
          [state.selectedResource.id], // userIds array
          state.patientTimezone || undefined // Only pass if timezone is available
        );
        
        // Convert resourceTimeSlots to flat timeSlots array
        const timeSlots = convertToDisplayTimeSlots(response.data.resourceTimeSlots);
        
        setState(prev => ({ 
          ...prev, 
          timeSlots,
          bookedAppointments: response.data.bookedAppointments,
          loadingTimeSlots: false 
        }));
      } else if (state.appointmentType === 'in-person' && state.selectedFacility) {
        const response = await appointmentSchedulingService.getFacilityTimeSlots(
          userId,
          state.selectedFacility.clinic_facility_id,
          date,
          token
        );
        
        // Convert facility time slots to display format
        const displayTimeSlots: DisplayTimeSlot[] = response.data.timeSlots.map(slot => ({
          availabilityDayTimeId: slot.availabilityDayTimeId,
          startTime: slot.startTime,
          endTime: slot.endTime,
          displayTime: slot.displayTime,
          bookedCount: slot.bookedCount
        }));
        
        setState(prev => ({ 
          ...prev, 
          timeSlots: displayTimeSlots,
          bookedAppointments: response.data.bookedAppointments,
          loadingTimeSlots: false 
        }));
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setState(prev => ({ 
        ...prev, 
        loadingTimeSlots: false, 
        timeSlots: [],
        bookedAppointments: [],
        error: 'Failed to fetch time slots' 
      }));
    }
  }, [user?.id, token, state.appointmentType, state.selectedResource, state.selectedFacility, state.patientTimezone]);

  // Handle resource selection
  const handleResourceSelect = useCallback((resource: Resource) => {
    setState(prev => ({ 
      ...prev, 
      selectedResource: resource,
      selectedDate: undefined,
      selectedTimeRange: null,
      availableDates: [],
      timeSlots: [],
      bookedAppointments: [],
      conflictWarning: null
    }));
    setCurrentMonth(new Date());
  }, []);

  // Handle facility selection
  const handleFacilitySelect = useCallback((facility: Facility) => {
    setState(prev => ({ 
      ...prev, 
      selectedFacility: facility,
      selectedDate: undefined,
      selectedTimeRange: null,
      availableDates: [],
      timeSlots: [],
      bookedAppointments: [],
      conflictWarning: null
    }));
    setCurrentMonth(new Date());
  }, []);

  // Handle date selection
  const handleDateSelect = useCallback((date: Date | undefined) => {
    setState(prev => ({ 
      ...prev, 
      selectedDate: date,
      selectedTimeRange: null,
      timeSlots: [],
      bookedAppointments: [],
      conflictWarning: null
    }));

    if (date) {
      // Use format instead of toISOString to avoid UTC conversion shifting the date
      const formattedDate = format(date, 'yyyy-MM-dd');
      fetchTimeSlots(formattedDate);
    }
  }, [fetchTimeSlots]);

  // Check for conflicts with booked appointments
  const checkConflicts = useCallback((startTime: string, endTime: string): string | null => {
    const parseTime = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const newStart = parseTime(startTime);
    const newEnd = parseTime(endTime);

    const conflicts = state.bookedAppointments.filter(apt => {
      const aptStart = new Date(apt.startDateTime);
      const aptEnd = new Date(apt.endDateTime);
      const aptStartTime = aptStart.getHours() * 60 + aptStart.getMinutes();
      const aptEndTime = aptEnd.getHours() * 60 + aptEnd.getMinutes();

      // Check if there's an overlap
      return (newStart < aptEndTime && newEnd > aptStartTime);
    });

    if (conflicts.length > 0) {
      return `Warning: ${conflicts.length} appointment(s) already booked in this time range.`;
    }
    return null;
  }, [state.bookedAppointments]);

  // Handle time range selection
  const handleTimeRangeSelect = useCallback((timeRange: SelectedTimeRange | null) => {
    if (timeRange) {
      const warning = checkConflicts(timeRange.startTime, timeRange.endTime);
      setState(prev => ({ 
        ...prev, 
        selectedTimeRange: timeRange,
        conflictWarning: warning
      }));
    } else {
      setState(prev => ({ 
        ...prev, 
        selectedTimeRange: null,
        conflictWarning: null
      }));
    }
  }, [checkConflicts]);

  // Handle month change in calendar
  const handleMonthChange = useCallback((newMonth: Date) => {
    setCurrentMonth(newMonth);
    fetchAvailableDates(newMonth.getFullYear(), newMonth.getMonth() + 1);
  }, [fetchAvailableDates]);

  // Auto-fetch resources when type is virtual
  useEffect(() => {
    if (state.appointmentType === 'virtual' && state.resources.length === 0) {
      fetchResources();
    }
  }, [state.appointmentType, state.resources.length, fetchResources]);

  // Auto-fetch facilities when type is in-person
  useEffect(() => {
    if (state.appointmentType === 'in-person' && state.facilities.length === 0 && clinicId) {
      fetchFacilities();
    }
  }, [state.appointmentType, state.facilities.length, clinicId, fetchFacilities]);

  // Auto-fetch dates when resource/facility is selected
  useEffect(() => {
    if (
      (state.appointmentType === 'virtual' && state.selectedResource) ||
      (state.appointmentType === 'in-person' && state.selectedFacility)
    ) {
      fetchAvailableDates(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
    }
  }, [state.selectedResource, state.selectedFacility, state.appointmentType, fetchAvailableDates, currentMonth]);

  // Get combined time range from all time slots
  const getCombinedTimeRange = useCallback(() => {
    if (state.timeSlots.length === 0) return null;

    // Sort by start time
    const sorted = [...state.timeSlots].sort((a, b) => {
      const [aH, aM] = a.startTime.split(':').map(Number);
      const [bH, bM] = b.startTime.split(':').map(Number);
      return (aH * 60 + aM) - (bH * 60 + bM);
    });

    const minStart = sorted[0].startTime;
    const maxEnd = sorted.reduce((max, slot) => {
      const [maxH, maxM] = max.split(':').map(Number);
      const [endH, endM] = slot.endTime.split(':').map(Number);
      return (endH * 60 + endM) > (maxH * 60 + maxM) ? slot.endTime : max;
    }, sorted[0].endTime);

    return {
      startTime: minStart,
      endTime: maxEnd,
      availabilityDayTimeId: sorted[0].availabilityDayTimeId
    };
  }, [state.timeSlots]);

  // Reset all appointment scheduling state
  const resetAppointmentScheduling = useCallback(() => {
    setState({
      appointmentType: null,
      selectedResource: null,
      selectedFacility: null,
      selectedDate: undefined,
      selectedTimeRange: null,
      resources: [],
      facilities: [],
      availableDates: [],
      timeSlots: [],
      bookedAppointments: [],
      patientTimezone: patientTimezone || '',
      loadingResources: false,
      loadingFacilities: false,
      loadingDates: false,
      loadingTimeSlots: false,
      error: null,
      conflictWarning: null
    });
    setCurrentMonth(new Date());
  }, []);

  return {
    ...state,
    currentMonth,
    handleAppointmentTypeChange,
    handleResourceSelect,
    handleFacilitySelect,
    handleDateSelect,
    handleTimeRangeSelect,
    handleMonthChange,
    getCombinedTimeRange,
    resetAppointmentScheduling,
    fetchResources,
    fetchFacilities
  };
};
